'use client'

import { nanoid } from 'nanoid'
import packageJson from '../package.json'

export type TracewayReplayMode = 'off' | 'exceptions-only' | 'session'

export type TracewayConfig = {
  enabled: boolean
  endpoint: string | null
  environment: string
  projectToken: string | null
  release: string
  replayMode: TracewayReplayMode
  serverName: string
}

export type TracewayBreadcrumb = {
  at: string
  category: string
  data: Record<string, unknown>
  message: string
}

export type TracewayAction = {
  at: string
  category: string
  data: Record<string, unknown>
  message: string
}

export type TracewayTraceRecord = {
  attributes?: Record<string, string | number | boolean | null>
  bodySize: number
  duration: number
  endpoint: string
  id: string
  isTask: false
  recordedAt: string
  statusCode: number
}

export type ExceptionStackTrace = {
  attributes?: Record<string, string | number | boolean | null>
  isMessage: boolean
  recordedAt: string
  sessionId?: string
  stackTrace: string
  traceId?: string
}

export type SessionRecording = {
  actions?: TracewayAction[]
  endedAt?: string
  events: unknown[]
  exceptionId?: string
  logs?: unknown[]
  segmentIndex?: number
  sessionId?: string
  startedAt?: string
}

export type CollectionFrame = {
  metrics: unknown[]
  sessionRecordings?: SessionRecording[]
  sessions?: unknown[]
  stackTraces: ExceptionStackTrace[]
  traces: TracewayTraceRecord[]
}

export type TracewayReport = {
  appVersion: string
  collectionFrames: CollectionFrame[]
  serverName: string
}

type TracewayReplayState = {
  actions: TimestampedTracewayAction[]
  eventBuffer: TimestampedReplayEvent[]
  recorderStop: (() => void) | null
  sessionId: string
  segmentIndex: number
  startedAt: string
  traces: TimestampedTracewayTraceRecord[]
}

type TracewayState = {
  breadcrumbs: TracewayBreadcrumb[]
  config: TracewayConfig
  replay: TracewayReplayState
  sessionId: string
  traceId: string
}

type TimestampedReplayEvent = {
  at: string
  event: unknown
}

type TimestampedTracewayAction = TracewayAction

type TimestampedTracewayTraceRecord = TracewayTraceRecord

const TRACEWAY_ENABLED = process.env.NEXT_PUBLIC_TRACEWAY_ENABLED === 'true'
const TRACEWAY_ENDPOINT = process.env.NEXT_PUBLIC_TRACEWAY_ENDPOINT?.trim() || ''
const TRACEWAY_PROJECT_TOKEN = process.env.NEXT_PUBLIC_TRACEWAY_PROJECT_TOKEN?.trim() || ''
const TRACEWAY_ENVIRONMENT =
  process.env.NEXT_PUBLIC_TRACEWAY_ENVIRONMENT?.trim() || process.env.NODE_ENV || 'development'
const TRACEWAY_RELEASE =
  process.env.NEXT_PUBLIC_TRACEWAY_RELEASE?.trim() ||
  process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA?.trim() ||
  process.env.GIT_COMMIT_SHA?.trim() ||
  packageJson.version ||
  '0.0.0'
const TRACEWAY_REPLAY_MODE =
  (process.env.NEXT_PUBLIC_TRACEWAY_REPLAY_MODE?.trim().toLowerCase() || 'exceptions-only') as TracewayReplayMode

const TRACEWAY_BREADCRUMB_LIMIT = 48
const TRACEWAY_ACTION_LIMIT = 240
const TRACEWAY_TRACE_LIMIT = 96
const TRACEWAY_EVENT_LIMIT = 1200
const TRACEWAY_REPLAY_WINDOW_MS = 10_000
const TRACEWAY_SESSION_STORAGE_KEY = 'askjyotish.traceway.session_id'
const TRACEWAY_ALLOWED_ORIGINS = new Set<string>()

let state: TracewayState | null = null
let nativeFetch: typeof fetch | null = null
let fetchPatched = false
let historyPatched = false
let lifecyclePatched = false
let xhrPatched = false

function ensureTracewayOriginSet() {
  if (typeof window === 'undefined') {
    return
  }

  if (TRACEWAY_ALLOWED_ORIGINS.size > 0) {
    return
  }

  TRACEWAY_ALLOWED_ORIGINS.add(window.location.origin)

  const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL?.trim() || ''
  if (apiBaseUrl) {
    try {
      TRACEWAY_ALLOWED_ORIGINS.add(new URL(apiBaseUrl, window.location.origin).origin)
    } catch {
      // Ignore malformed API origins and keep the local origin only.
    }
  }
}

function ensureUuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${nanoid(8)}-${nanoid(4)}-${nanoid(4)}-${nanoid(4)}-${nanoid(12)}`
}

function ensureHexId(length = 32) {
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(Math.ceil(length / 2)))
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('').slice(0, length)
  }

  return nanoid(length)
    .replace(/[^a-f0-9]/gi, 'a')
    .toLowerCase()
    .slice(0, length)
}

function ensureTraceId() {
  return ensureHexId(32)
}

function ensureSessionId() {
  if (typeof window === 'undefined') {
    return ensureUuid()
  }

  try {
    const cached = window.sessionStorage.getItem(TRACEWAY_SESSION_STORAGE_KEY)
    if (cached) {
      return cached
    }
    const nextSessionId = ensureUuid()
    window.sessionStorage.setItem(TRACEWAY_SESSION_STORAGE_KEY, nextSessionId)
    return nextSessionId
  } catch {
    return ensureUuid()
  }
}

function getTracewayEndpoint() {
  return TRACEWAY_ENDPOINT ? TRACEWAY_ENDPOINT.replace(/\/+$/, '') : null
}

function getTracewayProjectToken() {
  return TRACEWAY_PROJECT_TOKEN || null
}

function getTracewayMode(): TracewayReplayMode {
  if (TRACEWAY_REPLAY_MODE === 'off' || TRACEWAY_REPLAY_MODE === 'session' || TRACEWAY_REPLAY_MODE === 'exceptions-only') {
    return TRACEWAY_REPLAY_MODE
  }
  return 'exceptions-only'
}

function normalizeUrl(input: string) {
  try {
    return new URL(input, window.location.href)
  } catch {
    return null
  }
}

function getAllowedOrigins() {
  ensureTracewayOriginSet()
  return TRACEWAY_ALLOWED_ORIGINS
}

function isTracewayReportingUrl(url: URL) {
  const current = getTracewayState()
  if (!current?.config.endpoint) {
    return false
  }

  return url.href.startsWith(current.config.endpoint)
}

function shouldInstrumentRequest(url: URL) {
  const current = getTracewayState()
  if (!current?.config.enabled) {
    return false
  }

  if (!getAllowedOrigins().has(url.origin)) {
    return false
  }

  if (isTracewayReportingUrl(url)) {
    return false
  }

  return true
}

function getCurrentRoute() {
  if (typeof window === 'undefined') {
    return '/'
  }
  return window.location.pathname || '/'
}

function sanitizeValue(value: unknown, depth = 0): unknown {
  if (value == null) {
    return value
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value
  }

  if (typeof value === 'bigint') {
    return value.toString()
  }

  if (typeof value === 'function' || typeof value === 'symbol') {
    return String(value)
  }

  if (value instanceof Error) {
    return {
      message: value.message,
      name: value.name,
      stack: value.stack ?? null,
    }
  }

  if (value instanceof URL) {
    return value.toString()
  }

  if (Array.isArray(value)) {
    if (depth >= 2) {
      return value.slice(0, 12).map(() => '[depth-limit]')
    }
    return value.slice(0, 12).map((item) => sanitizeValue(item, depth + 1))
  }

  if (depth >= 2) {
    return '[depth-limit]'
  }

  const entries = Object.entries(value as Record<string, unknown>).slice(0, 24)
  return entries.reduce<Record<string, unknown>>((accumulator, [key, item]) => {
    accumulator[key] = sanitizeValue(item, depth + 1)
    return accumulator
  }, {})
}

function normalizeReplayData(
  data: Record<string, unknown>,
): Record<string, string | number | boolean | null> {
  const payload: Record<string, string | number | boolean | null> = {}

  for (const [key, value] of Object.entries(data)) {
    const sanitized = sanitizeValue(value)
    if (sanitized === null) {
      payload[key] = null
    } else if (typeof sanitized === 'string' || typeof sanitized === 'number' || typeof sanitized === 'boolean') {
      payload[key] = sanitized
    }
  }

  return payload
}

function createTracewayState(): TracewayState | null {
  if (typeof window === 'undefined') {
    return null
  }

  const endpoint = getTracewayEndpoint()
  const projectToken = getTracewayProjectToken()
  if (!TRACEWAY_ENABLED || !endpoint) {
    return null
  }

  return {
    breadcrumbs: [],
    config: {
      enabled: true,
      endpoint,
      environment: TRACEWAY_ENVIRONMENT,
      projectToken,
      release: TRACEWAY_RELEASE,
      replayMode: getTracewayMode(),
      serverName: window.location.hostname || TRACEWAY_ENVIRONMENT,
    },
    replay: {
      actions: [],
      eventBuffer: [],
      recorderStop: null,
      sessionId: ensureSessionId(),
      segmentIndex: 0,
      startedAt: new Date().toISOString(),
      traces: [],
    },
    sessionId: ensureSessionId(),
    traceId: ensureTraceId(),
  }
}

function getTracewayState() {
  if (state) {
    return state
  }

  const next = createTracewayState()
  if (!next) {
    return null
  }

  state = next
  patchFetch()
  patchXmlHttpRequest()
  patchHistory()
  patchLifecycleListeners()
  void startTracewayRecorder(next)
  enqueueBreadcrumb('traceway', 'initialized', {
    environment: next.config.environment,
    release: next.config.release,
    replay_mode: next.config.replayMode,
    serverName: next.config.serverName,
  })

  return state
}

async function startTracewayRecorder(current: TracewayState) {
  if (current.config.replayMode === 'off' || current.replay.recorderStop || typeof window === 'undefined') {
    return
  }

  try {
    const { record } = await import('@rrweb/record')
    const stopRecording = record({
      emit(event: unknown) {
        const at = new Date().toISOString()
        current.replay.eventBuffer.push({ at, event })
        if (current.replay.eventBuffer.length > TRACEWAY_EVENT_LIMIT) {
          current.replay.eventBuffer.splice(0, current.replay.eventBuffer.length - TRACEWAY_EVENT_LIMIT)
        }
      },
    })
    current.replay.recorderStop = typeof stopRecording === 'function' ? stopRecording : null
  } catch {
    // Replay should remain best-effort. Missing rrweb support must not block the app.
  }
}

function appendRingBuffer<T>(buffer: T[], value: T, limit: number) {
  buffer.push(value)
  if (buffer.length > limit) {
    buffer.splice(0, buffer.length - limit)
  }
}

function enqueueBreadcrumb(category: string, message: string, data: Record<string, unknown> = {}) {
  const current = getTracewayState()
  if (!current) {
    return
  }

  const at = new Date().toISOString()
  const normalizedData = normalizeReplayData({
    ...data,
    route: getCurrentRoute(),
    session_id: current.sessionId,
    trace_id: current.traceId,
  })

  const breadcrumb: TracewayBreadcrumb = {
    at,
    category,
    data: normalizedData,
    message,
  }
  appendRingBuffer(current.breadcrumbs, breadcrumb, TRACEWAY_BREADCRUMB_LIMIT)
  appendRingBuffer(
    current.replay.actions,
    {
      at,
      category,
      data: normalizedData,
      message,
    },
    TRACEWAY_ACTION_LIMIT,
  )
}

function buildTracewayHeaders(url: URL, method: string) {
  const current = getTracewayState()
  if (!current) {
    return {}
  }

  const spanId = ensureHexId(16)
  const traceparent = `00-${current.traceId}-${spanId}-01`

  return {
    traceparent,
    'x-traceway-environment': current.config.environment,
    'x-traceway-method': method,
    'x-traceway-path': url.pathname,
    'x-traceway-release': current.config.release,
    'x-traceway-session-id': current.sessionId,
    'x-traceway-span-id': spanId,
    'x-traceway-trace-id': current.traceId,
  }
}

function reportRequestTelemetry(
  url: URL,
  method: string,
  startedAt: number,
  status: number | null,
  error: unknown | null,
  spanId: string,
) {
  const current = getTracewayState()
  if (!current) {
    return
  }

  const durationMs = Math.max(0, Math.round(performance.now() - startedAt))
  const at = new Date().toISOString()
  const path = url.pathname
  const durationNs = durationMs * 1_000_000
  const traceRecord: TracewayTraceRecord = {
    attributes: {
      environment: current.config.environment,
      method,
      path,
      request_id: spanId,
      route: getCurrentRoute(),
      session_id: current.sessionId,
      span_id: spanId,
      trace_id: current.traceId,
    },
    bodySize: 0,
    duration: durationNs,
    endpoint: `${method.toUpperCase()} ${path}`,
    id: ensureUuid(),
    isTask: false,
    recordedAt: at,
    statusCode: status ?? 0,
  }
  appendRingBuffer(current.replay.traces, traceRecord, TRACEWAY_TRACE_LIMIT)

  const requestMessage =
    error || (status != null && status >= 400) ? 'request failed' : 'request completed'

  enqueueBreadcrumb('request', requestMessage, {
    duration_ms: durationMs,
    method,
    path,
    route: getCurrentRoute(),
    span_id: spanId,
    status,
  })
}

function patchFetch() {
  if (fetchPatched || typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return
  }

  nativeFetch = window.fetch.bind(window)
  fetchPatched = true

  window.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
    const request = input instanceof Request ? input : new Request(input, init)
    const requestUrl = normalizeUrl(request.url)
    if (!requestUrl || !shouldInstrumentRequest(requestUrl)) {
      return nativeFetch!(input as RequestInfo | URL, init)
    }

    const headers = new Headers(request.headers)
    const traceHeaders = buildTracewayHeaders(requestUrl, request.method)
    for (const [key, value] of Object.entries(traceHeaders)) {
      headers.set(key, value)
    }

    const spanId = traceHeaders['x-traceway-span-id'] ?? ensureHexId(16)
    const instrumentedRequest = new Request(request, { headers })
    const startedAt = performance.now()

    enqueueBreadcrumb('request', 'request started', {
      method: request.method,
      path: requestUrl.pathname,
      span_id: spanId,
    })

    try {
      const response = await nativeFetch!(instrumentedRequest)
      reportRequestTelemetry(requestUrl, request.method, startedAt, response.status, null, spanId)
      return response
    } catch (error) {
      reportRequestTelemetry(requestUrl, request.method, startedAt, null, error, spanId)
      throw error
    }
  }) as typeof window.fetch
}

function patchXmlHttpRequest() {
  if (xhrPatched || typeof window === 'undefined' || typeof XMLHttpRequest === 'undefined') {
    return
  }

  xhrPatched = true
  const nativeOpen = XMLHttpRequest.prototype.open
  const nativeSend = XMLHttpRequest.prototype.send
  const nativeSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader
  const metadata = new WeakMap<
    XMLHttpRequest,
    {
      method: string
      requestUrl: URL | null
      spanId: string
      startedAt: number
    }
  >()

  XMLHttpRequest.prototype.open = function open(method: string, url: string | URL, ...rest: any[]) {
    const requestUrl = typeof url === 'string' ? normalizeUrl(url) : normalizeUrl(url.toString())
    metadata.set(this, {
      method,
      requestUrl,
      spanId: ensureHexId(16),
      startedAt: 0,
    })
    return (nativeOpen as (...args: any[]) => void).apply(this, [method, url, ...rest])
  }

  XMLHttpRequest.prototype.send = function send(body?: XMLHttpRequestBodyInit | null) {
    const entry = metadata.get(this)
    if (entry?.requestUrl && shouldInstrumentRequest(entry.requestUrl)) {
      const traceHeaders = buildTracewayHeaders(entry.requestUrl, entry.method)
      let reported = false

      for (const [key, value] of Object.entries(traceHeaders)) {
        try {
          nativeSetRequestHeader.call(this, key, value)
        } catch {
          // Some browsers reject late header writes for CORS or closed requests.
        }
      }

      entry.startedAt = performance.now()
      enqueueBreadcrumb('request', 'xhr started', {
        method: entry.method,
        path: entry.requestUrl.pathname,
        span_id: entry.spanId,
      })

      const finalize = () => {
        if (reported) {
          return
        }
        reported = true
        reportRequestTelemetry(
          entry.requestUrl as URL,
          entry.method,
          entry.startedAt,
          this.status || null,
          null,
          entry.spanId,
        )
      }

      this.addEventListener('loadend', finalize, { once: true })
      this.addEventListener(
        'error',
        () => {
          if (reported) {
            return
          }
          reported = true
          reportRequestTelemetry(
            entry.requestUrl as URL,
            entry.method,
            entry.startedAt,
            null,
            new Error('XMLHttpRequest error'),
            entry.spanId,
          )
        },
        { once: true },
      )
    }

    return nativeSend.call(this, body)
  }
}

function patchHistory() {
  if (historyPatched || typeof window === 'undefined') {
    return
  }

  historyPatched = true
  const nativePushState = window.history.pushState.bind(window.history)
  const nativeReplaceState = window.history.replaceState.bind(window.history)

  function recordRouteChange(action: 'pushState' | 'replaceState' | 'popstate') {
    enqueueBreadcrumb('navigation', action, {
      route: getCurrentRoute(),
    })
  }

  window.history.pushState = function pushState(...args) {
    const result = nativePushState(...args)
    recordRouteChange('pushState')
    return result
  }

  window.history.replaceState = function replaceState(...args) {
    const result = nativeReplaceState(...args)
    recordRouteChange('replaceState')
    return result
  }

  window.addEventListener('popstate', () => {
    recordRouteChange('popstate')
  })
}

function patchLifecycleListeners() {
  if (lifecyclePatched || typeof window === 'undefined') {
    return
  }

  lifecyclePatched = true

  window.addEventListener('error', (event) => {
    const error =
      event.error instanceof Error
        ? event.error
        : new Error(event.message || 'Window error')

    enqueueBreadcrumb('exception', 'window error', {
      colno: event.colno,
      filename: event.filename,
      lineno: event.lineno,
    })
    void captureTracewayException(error, {
      colno: event.colno,
      filename: event.filename,
      lineno: event.lineno,
      source: 'window.onerror',
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(typeof event.reason === 'string' ? event.reason : 'Unhandled promise rejection')
    enqueueBreadcrumb('exception', 'unhandledrejection', {})
    void captureTracewayException(error, {
      source: 'window.unhandledrejection',
    })
  })

  window.addEventListener('visibilitychange', () => {
    enqueueBreadcrumb('lifecycle', 'visibility change', {
      hidden: document.hidden,
    })
  })

  window.addEventListener('online', () => {
    enqueueBreadcrumb('lifecycle', 'online', {
      online: true,
    })
  })

  window.addEventListener('offline', () => {
    enqueueBreadcrumb('lifecycle', 'offline', {
      online: false,
    })
  })
}

function snapshotReplayWindow(current: TracewayState, recordedAt: string) {
  const cutoff = Date.parse(recordedAt) - TRACEWAY_REPLAY_WINDOW_MS

  const traces = current.replay.traces.filter((entry) => Date.parse(entry.recordedAt) >= cutoff)
  const actions = current.replay.actions.filter((entry) => Date.parse(entry.at) >= cutoff)
  const events = current.replay.eventBuffer.filter((entry) => Date.parse(entry.at) >= cutoff).map((entry) => entry.event)

  const startedAt =
    current.replay.eventBuffer.find((entry) => Date.parse(entry.at) >= cutoff)?.at ??
    current.replay.actions.find((entry) => Date.parse(entry.at) >= cutoff)?.at ??
    current.replay.traces.find((entry) => Date.parse(entry.recordedAt) >= cutoff)?.recordedAt ??
    current.replay.startedAt

  return {
    actions,
    events,
    startedAt,
    traces,
  }
}

function buildStackTrace(error: unknown) {
  if (error instanceof Error) {
    const header = `${error.name || 'Error'}: ${error.message || 'Unknown error'}`
    if (!error.stack) {
      return header
    }
    const lines = error.stack.split('\n').map((line) => line.trimEnd())
    if (lines.length === 0) {
      return header
    }
    if (lines[0]?.startsWith(error.name)) {
      return error.stack
    }
    return [header, ...lines.slice(1)].join('\n')
  }

  return `${typeof error}: ${String(error)}`
}

function buildExceptionRecord(
  error: unknown,
  current: TracewayState,
  recordedAt: string,
  sessionId: string,
  exceptionId: string,
  context: Record<string, unknown>,
): ExceptionStackTrace {
  const safeContext = normalizeReplayData({
    ...context,
    exception_id: exceptionId,
    release: current.config.release,
    replay_mode: current.config.replayMode,
    route: getCurrentRoute(),
    session_id: sessionId,
    trace_id: current.traceId,
  })

  return {
    attributes: safeContext,
    isMessage: false,
    recordedAt,
    sessionId,
    stackTrace: buildStackTrace(error),
    traceId: current.traceId,
  }
}

function buildSessionRecording(
  snapshot: ReturnType<typeof snapshotReplayWindow>,
  current: TracewayState,
  recordedAt: string,
  exceptionId: string,
  sessionId: string,
) {
  if (current.config.replayMode === 'off' || snapshot.events.length === 0) {
    return null
  }

  const recording: SessionRecording = {
    actions: snapshot.actions,
    endedAt: recordedAt,
    events: snapshot.events,
    exceptionId,
    logs: [],
    segmentIndex: current.replay.segmentIndex,
    sessionId,
    startedAt: snapshot.startedAt,
  }

  current.replay.segmentIndex += 1
  return recording
}

async function postTracewayReport(report: TracewayReport, keepalive = true) {
  const current = getTracewayState()
  if (!current?.config.endpoint) {
    return
  }

  const body = JSON.stringify(report)
  const requestInit: RequestInit = {
    body,
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    keepalive,
    method: 'POST',
  }
  if (current.config.projectToken) {
    requestInit.headers = {
      ...requestInit.headers,
      Authorization: `Bearer ${current.config.projectToken}`,
    }
  }

  try {
    if (nativeFetch) {
      await nativeFetch(current.config.endpoint, requestInit)
      return
    }

    await fetch(current.config.endpoint, requestInit)
  } catch {
    // Reporting must never block rendering or break the exception surface.
  }
}

export function initializeTracewayClient() {
  return getTracewayState()
}

export function getTracewayRuntime() {
  return getTracewayState()
}

export function getTracewayRelease() {
  return getTracewayState()?.config.release ?? TRACEWAY_RELEASE
}

export function getTracewayHeaders(url: string, method = 'GET') {
  const requestUrl = normalizeUrl(url)
  if (!requestUrl || !shouldInstrumentRequest(requestUrl)) {
    return {}
  }

  return buildTracewayHeaders(requestUrl, method)
}

export function recordTracewayBreadcrumb(
  category: string,
  message: string,
  data: Record<string, unknown> = {},
) {
  if (!getTracewayState()) {
    return
  }

  enqueueBreadcrumb(category, message, data)
}

export async function captureTracewayException(
  error: unknown,
  context: Record<string, unknown> = {},
) {
  const current = getTracewayState()
  if (!current) {
    return
  }

  const recordedAt = new Date().toISOString()
  const exceptionId = ensureUuid()
  const sessionId = current.sessionId
  const snapshot = snapshotReplayWindow(current, recordedAt)
  const stackTrace = buildExceptionRecord(error, current, recordedAt, sessionId, exceptionId, context)
  const sessionRecording = buildSessionRecording(snapshot, current, recordedAt, exceptionId, sessionId)
  const collectionFrame: CollectionFrame = {
    metrics: [],
    stackTraces: [stackTrace],
    traces: snapshot.traces,
  }

  if (sessionRecording) {
    collectionFrame.sessionRecordings = [sessionRecording]
  }

  enqueueBreadcrumb('exception', 'captured', {
    boundary: context.boundary ?? 'browser',
    error_name: error instanceof Error ? error.name : typeof error,
    source: typeof context.source === 'string' ? context.source : 'browser',
  })

  await postTracewayReport({
    appVersion: current.config.release,
    collectionFrames: [collectionFrame],
    serverName: current.config.serverName,
  })
}

export function recordTracewayNavigation(
  kind: 'route-change' | 'route-entry' | 'route-exit',
  route: string,
  context: Record<string, unknown> = {},
) {
  recordTracewayBreadcrumb('navigation', kind, {
    ...context,
    route: route.split('?')[0] || route,
  })
}

export function recordTracewayAuthEvent(message: string, data: Record<string, unknown> = {}) {
  recordTracewayBreadcrumb('auth', message, data)
}

export function recordTracewayChatEvent(message: string, data: Record<string, unknown> = {}) {
  recordTracewayBreadcrumb('chat', message, data)
}

export function recordTracewayWebSocketEvent(message: string, data: Record<string, unknown> = {}) {
  recordTracewayBreadcrumb('websocket', message, data)
}

export function recordTracewayRequestEvent(
  message: string,
  data: Record<string, unknown> = {},
) {
  recordTracewayBreadcrumb('request', message, data)
}

export function getTracewaySessionId() {
  return getTracewayState()?.sessionId ?? null
}
